import type { ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { VIEW_LABELS, VIEW_ORDER, type CalendarView } from '../lib/viewState';
import { DraftsButton, DRAFTS_IN_CALENDAR_PATH, NewProjectButton } from '@/features/projects';
import { cn } from '@/shared/lib/utils';

interface CalendarToolbarProps {
  /** `Agosto 2026` — já formatado por `formatMonthYear` (decisão 80). */
  title: string;
  view: CalendarView;
  onViewChange: (view: CalendarView) => void;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  /** O botão que abre a folha de filtros — mora COLADO no mês (§18). */
  filterButton: ReactNode;
  /** O que abre folha só abaixo de `lg`: hoje, `Precisa de você` (§2.2). */
  compactActions: ReactNode;
}

/**
 * A barra da grade: **o mês é o título da TELA** (spec-calendar-ux.md §2.1,
 * decisão 80).
 *
 * ⚠️ **Este `<h1>` era um `<h2>`, e a promoção é a decisão.** A área de conteúdo
 * deixa de nomear o lugar: não existe mais um "Calendário" escrito na tela —
 * quem diz onde a pessoa está é o item aceso na navegação. É o que o Calendário
 * do macOS faz, e o risco está dito em voz alta na spec: quem chega por link
 * externo lê "Agosto 2026" e só a barra lateral responde "onde é isto?".
 *
 * **O título não é o que o FullCalendar escreve.** Aquele muda de anatomia com a
 * vista ("agosto de 2026", "11 – 17 de agosto de 2026"), e um cabeçalho de tela
 * que troca de formato a cada botão obriga a reler. Quem formata é
 * `shared/lib/dates.ts` (regra 9), sempre no fuso da igreja.
 *
 * **Dois níveis, e a divisão é de natureza** (§18, decisão 106): em cima o que
 * se FAZ (a vista, e o que cria ou retoma um projeto); embaixo ONDE se está (o
 * mês, o recorte e a navegação no tempo).
 *
 * ⚠️ **Eram três linhas, e a do meio era a faixa dos quatro `<select>`.** Ela
 * sumiu junto com os seletores: o recorte agora mora numa folha, aberta pelo
 * botão colado no mês (`CalendarFilterSheet`). O que a faixa custava era altura
 * — três linhas de cromo antes da primeira semana do mês, no lugar onde a grade
 * precisa de todo pixel que sobrar.
 *
 * As setas ficam COLADAS ao "Hoje", como no macOS: os três controlam a mesma
 * coisa (onde estou no tempo), e separá-los faria "Hoje" parecer um filtro. Eles
 * descem para a linha de baixo porque é lá que está o mês que eles mudam — o
 * controle ao lado do seu efeito.
 */
export function CalendarToolbar({
  title,
  view,
  onViewChange,
  onPrev,
  onNext,
  onToday,
  filterButton,
  compactActions,
}: CalendarToolbarProps) {
  // `0.9rem` são os `0.75rem` de antes com 20% a mais: as duas linhas passaram a
  // carregar assunto diferente (ação em cima, lugar embaixo), e o respiro é o
  // que faz essa divisão ser lida sem precisar de um traço.
  return (
    <div className="shrink-0 space-y-[0.9rem]">
      {/*
        **Linha de cima: o que se FAZ.** A vista no meio, alinhada ao eixo da
        grade que ela reorganiza; o que cria e o que retoma, à direita.

        As três colunas são `1fr · auto · 1fr` justamente para que a vista fique
        centrada na grade, e não no espaço que sobra dos botões — centralizar
        com `justify-between` deslocaria a pastilha alguns pixels a cada vez que
        um rótulo da direita mudasse de tamanho.

        Abaixo de `sm` a grade vira uma coluna só: em 375px a vista e os dois
        botões não cabem lado a lado, e espremê-los cortaria "Novo projeto" ao
        meio.
      */}
      <div className="grid grid-cols-1 items-center gap-x-4 gap-y-3 sm:grid-cols-[1fr_auto_1fr]">
        <div aria-hidden="true" className="hidden sm:block" />

        <div className="flex justify-center">
          <ViewSwitch view={view} onChange={onViewChange} />
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2">
          {/* O atalho de criação mora na grade porque o encaixe acontece aqui
              (ADR-015 §6). O outro caminho é o botão direito sobre um dia, que
              leva a data preenchida.

              "Rascunhos" vem ANTES e em `outline`: é a volta para o que ficou
              pela metade, não o começo de nada — e ele só está aqui porque o
              item de menu "Minhas pendências" saiu (ver `DraftsButton`). O
              rascunho é a única coisa da esteira que a grade não mostra e a
              caixa de entrada não lista.

              O destino é o endereço de dentro do Calendário: quem clica aqui
              continua no Calendário, com a trilha oferecendo a volta para a
              grade. A lista é a mesma de `/rascunhos` — o que muda é de quem
              ela é enquanto a pessoa está nela. */}
          <DraftsButton to={DRAFTS_IN_CALENDAR_PATH} />
          <NewProjectButton />
        </div>
      </div>

      {/*
        **Linha de baixo: ONDE se está.** O mês, o recorte que o qualifica, e a
        navegação no tempo — as três coisas que respondem "que pedaço da agenda
        é este?". Elas descem porque são leitura, e a de cima é ação.

        O filtro fica colado no título de propósito: o mês diz o período, o botão
        diz *o que dentro dele*, e o contador ao lado do mês é o que impede que
        uma grade recortada seja lida como um mês vazio.
      */}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
        <div className="flex min-w-0 items-center gap-2">
          {/* `first-letter:uppercase`, e não `capitalize`: em português só a
              primeira palavra sobe, e `capitalize` deixaria "Agosto – Setembro"
              com as duas maiúsculas. */}
          <h1 className="min-w-0 truncate text-2xl font-semibold tracking-tight first-letter:uppercase">
            {title}
          </h1>
          {filterButton}
        </div>

        {/* `flex-wrap` e `justify-end`: em 375px a caixa de entrada e a
            navegação somam mais que a linha, e sem a quebra a seta do próximo
            período vazava pela borda direita da tela. */}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <div className="flex items-center gap-2 lg:hidden">{compactActions}</div>

          <div className="flex items-center gap-0.5 rounded-full border border-border bg-card p-0.5">
            <ArrowButton label="Período anterior" onClick={onPrev}>
              <ChevronLeft aria-hidden="true" className="size-4" />
            </ArrowButton>
            <button
              type="button"
              onClick={onToday}
              className="rounded-full px-3 py-1 text-sm font-medium transition-[background-color,transform] duration-fast ease-out hover:bg-accent active:scale-[0.97] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              Hoje
            </button>
            <ArrowButton label="Próximo período" onClick={onNext}>
              <ChevronRight aria-hidden="true" className="size-4" />
            </ArrowButton>
          </div>
        </div>
      </div>
    </div>
  );
}

function ArrowButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="rounded-full p-1.5 text-muted-foreground transition-[background-color,color,transform] duration-fast ease-out hover:bg-accent hover:text-foreground active:scale-[0.94] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
    >
      {children}
    </button>
  );
}

/**
 * O controle segmentado de vista.
 *
 * A pastilha do selecionado é **uma superfície que desliza**, e não um fundo que
 * troca de célula: o olho segue um objeto contínuo e perde um objeto que
 * pisca. Ela viaja em `transform`, que o compositor resolve sem relayout, e em
 * 180ms com a curva de saída da casa — tempo de o dedo sair do botão e o olho
 * reencontrar o rótulo.
 */
function ViewSwitch({
  view,
  onChange,
}: {
  view: CalendarView;
  onChange: (view: CalendarView) => void;
}) {
  const index = VIEW_ORDER.indexOf(view);

  return (
    <div
      role="group"
      aria-label="Forma de ver o calendário"
      className="relative flex rounded-full border border-border bg-muted p-0.5"
    >
      <span
        aria-hidden="true"
        className="absolute inset-y-0.5 left-0.5 rounded-full bg-card shadow-sm transition-transform duration-base ease-out"
        style={{
          width: `calc((100% - 0.25rem) / ${VIEW_ORDER.length})`,
          transform: `translateX(${index * 100}%)`,
        }}
      />
      {VIEW_ORDER.map((entry) => (
        <button
          key={entry}
          type="button"
          aria-pressed={entry === view}
          onClick={() => onChange(entry)}
          className={cn(
            'relative z-10 min-w-[3.5rem] rounded-full px-3 py-1 text-sm font-medium',
            'transition-colors duration-fast ease-out',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
            entry === view ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {VIEW_LABELS[entry]}
        </button>
      ))}
    </div>
  );
}
